/**
 * DataExportPanel — CSV / Excel export trigger for module_data_export.
 *
 * Phase 16 / Task 16.6 (DEC-163). Simple date-range picker + format buttons.
 * On success, opens the S3 signed URL in a new tab.
 */
import React from 'react';
import { Button } from 'src/components/ui/button';
import { useDisposalBiDataExport } from '../../hooks/useDisposalBiPanels';
import { PanelChrome } from './PanelChrome';

interface Props {
  readonly onModuleInactive?: () => void;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function DataExportPanel({ onModuleInactive }: Props): React.JSX.Element | null {
  const [dateFrom, setDateFrom] = React.useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = React.useState(isoToday());
  const mutation = useDisposalBiDataExport();

  React.useEffect(() => {
    if (mutation.isError && mutation.error !== null && mutation.error.message.includes('409')) {
      onModuleInactive?.();
    }
  }, [mutation.isError, mutation.error, onModuleInactive]);

  if (mutation.isError && mutation.error !== null && mutation.error.message.includes('409'))
    return null;

  const handleExport = (format: 'CSV' | 'EXCEL'): void => {
    mutation.mutate(
      { format, dateFrom, dateTo },
      {
        onSuccess: (data) => {
          window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
        },
      },
    );
  };

  return (
    <PanelChrome
      title="Data Export"
      description="Download raw ticket-level data for a date range (CSV or Excel)."
    >
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label
            htmlFor="bi-export-from"
            className="block text-xs text-[var(--color-text-tertiary)]"
          >
            From
          </label>
          <input
            id="bi-export-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-[10px] border border-input bg-card px-2 py-1 text-sm text-foreground"
          />
        </div>
        <div>
          <label htmlFor="bi-export-to" className="block text-xs text-[var(--color-text-tertiary)]">
            To
          </label>
          <input
            id="bi-export-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-[10px] border border-input bg-card px-2 py-1 text-sm text-foreground"
          />
        </div>
        <Button
          onClick={() => handleExport('CSV')}
          disabled={mutation.isPending}
          data-testid="bi-export-csv-button"
        >
          {mutation.isPending ? 'Preparing…' : 'Download CSV'}
        </Button>
        <Button
          onClick={() => handleExport('EXCEL')}
          disabled={mutation.isPending}
          variant="outline"
          data-testid="bi-export-excel-button"
        >
          {mutation.isPending ? 'Preparing…' : 'Download Excel'}
        </Button>
      </div>
      {mutation.isError && !mutation.error.message.includes('409') && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          Export failed. Try again.
        </p>
      )}
    </PanelChrome>
  );
}
